import axios from 'axios';
import { BASE_URL } from "../../../../Constants/constants";

// Helper function to get authorization config
const getAuthConfig = () => {
    console.log("Getting auth config...");
    const token = localStorage.getItem('accessToken');
    if (!token) {
        console.error("Authentication token is missing");
        throw new Error('Authentication token is missing.');
    }

    console.log("Auth token found, creating config");
    return {
        headers: { Authorization: `Bearer ${token}` },
    };
};

// Form validation function
export const validateEquipmentFormData = (formData) => {
    console.log("Validating equipment form data:", formData);
    const errors = {};

    // Helper function to check empty values
    const isEmptyValue = value => !value || value.toString().trim() === '';

    // Required field validations with specific messages
    if (isEmptyValue(formData.category)) {
        errors.category = 'Category field is required';
    }

    if (isEmptyValue(formData.make)) {
        errors.make = 'Make field is required';
    }

    if (isEmptyValue(formData.model)) {
        errors.model = 'Model field is required';
    }

    // Validate equipment selection
    if (isEmptyValue(formData.equipment_id)) {
        errors.equipment_id = 'Equipment selection is required';
    }

    // Validate serial number
    if (isEmptyValue(formData.serial_number)) {
        errors.serial_number = 'Serial number selection is required';
    }

    // Delivery type
    if (isEmptyValue(formData.delivery_type)) {
        errors.delivery_type = 'Delivery type is required';
    }

    // Date assigned
    if (isEmptyValue(formData.date_assigned)) {
        errors.date_assigned = 'Date assigned is required';
    }

    // Delivered by
    if (isEmptyValue(formData.delivered_by)) {
        errors.delivered_by = 'Delivered by is required';
    }

    // Device condition
    if (isEmptyValue(formData.device_condition)) {
        errors.device_condition = 'Device condition is required';
    }

    console.log("Validation errors:", errors);
    return errors;
};

// Helper function to sanitize payload
export const sanitizePayload = (data) => {
    console.log("Sanitizing payload:", data);
    return Object.fromEntries(
        Object.entries(data).map(([key, value]) => {
            if (key.includes("date") && (value === "" || value === null)) {
                // Ensure empty or null date fields are converted to null
                return [key, null];
            }
            if (value === "" || value === null) {
                // Keep non-date fields as empty strings
                return [key, ""];
            }
            return [key, value]; // Pass valid values unchanged
        })
    );
};

// Save equipment data
export const saveEquipmentData = async (equipment, currentUser) => {
    console.log("SAVING EQUIPMENT DATA:", equipment);

    try {
        const config = getAuthConfig();
        console.log("Auth config:", config);

        const sanitizedItem = sanitizePayload({
            ...equipment,
            user_id: currentUser.id
        });

        console.log("Sanitized payload:", sanitizedItem);

        let response;
        // Check for existing ID to determine if updating or creating
        if (equipment.id) {
            console.log(`Updating equipment with ID ${equipment.id}`);
            response = await axios.put(
                `${BASE_URL}/equipment/${equipment.id}`,
                sanitizedItem,
                config
            );
        } else {
            console.log("Creating new equipment");
            response = await axios.post(
                `${BASE_URL}/equipment`,
                sanitizedItem,
                config
            );
        }

        console.log("Save equipment response:", response);
        return response.data;
    } catch (error) {
        console.error('Save error details:', error.response?.data);
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
        throw error;
    }
};

// Fetch all equipment (both in stock and assigned)
export const fetchEquipmentData = async (currentUser) => {
    console.log("FETCH EQUIPMENT DATA CALLED with user:", currentUser);

    try {
        const config = getAuthConfig();
        console.log("Authorization config:", config);

        console.log("Making request to:", `${BASE_URL}/equipment/me`);
        const response = await axios.get(`${BASE_URL}/equipment/me`, config);
        console.log("Equipment data response:", response);

        // Ensure we return an array even if response.data is empty or undefined
        let equipmentData = response.data || [];

        // Double-check we have an array
        if (!Array.isArray(equipmentData)) {
            console.warn("API returned non-array data, converting to array:", equipmentData);
            equipmentData = [equipmentData];
        }

        if (equipmentData.length > 0) {
            console.log("Sample equipment item:", equipmentData[0]);
            console.log(`Fetched ${equipmentData.length} equipment items`);
        } else {
            console.log("Empty equipment data array returned");
        }

        return equipmentData;
    } catch (error) {
        console.error('Error fetching equipment data:', error);

        if (error.response) {
            console.error('Error response status:', error.response.status);
            console.error('Error response data:', error.response.data);

            // Handle 404 (no equipment data)
            if (error.response.status === 404) {
                console.log("Received 404 - no equipment found, returning empty array");
                return [];
            }
        }

        throw new Error('Failed to load equipment data. Please try again.');
    }
};

// Get a single asset by ID - UPDATED to use correct endpoint
export const getEquipmentById = async (equipmentId) => {
    console.log(`Getting asset details for ID: ${equipmentId}`);

    try {
        const config = getAuthConfig();
        // Use assets endpoint instead of equipment endpoint
        const response = await axios.get(`${BASE_URL}/assets/${equipmentId}/`, config);
        console.log("Asset details response:", response.data);
        return response.data;
    } catch (error) {
        console.error('Error fetching asset details:', error.response?.data || error.message);
        throw new Error('Failed to load asset details. Please try again.');
    }
};

// Assign equipment to a user - UPDATED to use the new backend endpoint
export const assignEquipment = async (equipmentId, assignmentData, currentUser) => {
    try {
        console.log(`Assigning asset ID ${equipmentId} to user:`, currentUser.id);
        console.log("Assignment data:", assignmentData);

        if (!equipmentId) {
            throw new Error('Asset ID is required for assignment');
        }

        // Convert to correct ID format - ensure it's a number
        const numericId = parseInt(equipmentId, 10);
        if (isNaN(numericId)) {
            throw new Error('Invalid asset ID format');
        }

        const config = getAuthConfig();

        // Create the payload for the new assign-from-asset endpoint
        const payload = sanitizePayload({
            asset_id: numericId,
            user_id: currentUser.id,
            delivery_type: assignmentData.delivery_type,
            device_condition: assignmentData.device_condition,
            date_assigned: assignmentData.date_assigned,
            delivered_by: assignmentData.delivered_by,
            recipient_signature: assignmentData.recipient_signature || "",
            head_of_ict_full_name: assignmentData.head_of_ict_full_name || "",
            head_ict_signature: assignmentData.head_ict_signature || ""
        });

        console.log("Assignment payload:", payload);

        // Use the new assign-from-asset endpoint
        const url = `${BASE_URL}/equipment/assign-from-asset`;
        console.log("Assignment endpoint:", url);

        // Create the equipment assignment
        const response = await axios.post(url, payload, config);

        console.log("Assignment response:", response);
        return response.data;
    } catch (error) {
        console.error('Error assigning equipment:', error);

        if (error.response) {
            console.error('Status:', error.response.status);
            console.error('Data:', error.response.data);
            console.error('Headers:', error.response.headers);

            // If the endpoint returns a 404, it means the equipment ID doesn't exist
            if (error.response.status === 404) {
                throw new Error('Asset not found. It may have been deleted or is not available for assignment.');
            }

            if (error.response.data && error.response.data.detail) {
                throw new Error(`Failed to assign equipment: ${error.response.data.detail}`);
            }
        }

        throw new Error('Failed to assign equipment. Please try again.');
    }
};

// Delete Equipment Data
export const deleteEquipmentData = async (equipmentId) => {
    console.log(`Deleting equipment with ID: ${equipmentId}`);

    try {
        const config = getAuthConfig();
        const response = await axios.delete(`${BASE_URL}/equipment/${equipmentId}/`, config);
        console.log("Delete response:", response);
        console.log(`Equipment with ID ${equipmentId} deleted successfully.`);
        return true;
    } catch (error) {
        console.error('Error deleting equipment:', error.response?.data || error.message);
        throw new Error('Failed to delete equipment data.');
    }
};

// UPDATED: Function to fetch in-stock equipment using assets endpoint
export const fetchInStockEquipment = async () => {
    console.log("FETCH IN-STOCK EQUIPMENT FUNCTION CALLED");

    try {
        const config = getAuthConfig();

        // Use the available assets endpoint instead of equipment/in-stock
        console.log("Making API request to:", `${BASE_URL}/assets/available/`);
        const response = await axios.get(`${BASE_URL}/assets/available/`, config);

        console.log("Available assets API Response received:", {
            status: response.status,
            statusText: response.statusText,
            hasData: !!response.data,
            dataLength: Array.isArray(response.data) ? response.data.length : 'not an array'
        });

        if (!response.data) {
            console.warn("API returned empty data");
            return [];
        }

        // Transform asset data to match equipment structure expected by frontend
        const transformedData = response.data.map(asset => ({
            id: asset.id,
            category: asset.asset_type,
            make: asset.brand,
            model: asset.model,
            serial_number: asset.serial_number,
            status_of_equipment: asset.status,
            delivery_type: "Assignment",
            device_condition: "Good",
            date_assigned: null,
            delivered_by: "",
            user_id: null
        }));

        console.log(`Transformed ${transformedData.length} assets to equipment format`);
        return transformedData;

    } catch (error) {
        console.error("ERROR IN fetchInStockEquipment:", error);

        // Return empty array for 404 (no data) or 403 (no permission)
        if (error.response?.status === 404 || error.response?.status === 403) {
            console.log("No assets available or no permission, returning empty array");
            return [];
        }

        throw new Error(`Failed to load available equipment: ${error.message}`);
    }
};

// Fetch asset options for dropdowns
export const fetchAssetOptions = async () => {
    // console.log("FETCH ASSET OPTIONS FUNCTION CALLED");
    // console.log("API URL format will be:", `${BASE_URL}/assets`);

    try {
        // Get authorization config
        const config = getAuthConfig();
        console.log("Authorization config:", config);

        // Make the API request
        // console.log("Making API request to:", `${BASE_URL}/assets`);
        const response = await axios.get(`${BASE_URL}/assets/`, config);

        // Log the response
        console.log("Asset API Response received:", {
            status: response.status,
            statusText: response.statusText,
            hasData: !!response.data,
            dataLength: Array.isArray(response.data) ? response.data.length : 'not an array'
        });

        // Check if response is valid
        if (!response.data) {
            console.warn("Asset API returned empty data");
            return [];
        }

        // Return the data
        return response.data;
    } catch (error) {
        console.error("ERROR IN fetchAssetOptions:", error);

        // Return empty array for 404 (no data)
        if (error.response?.status === 404) {
            return [];
        }

        throw new Error(`Failed to load asset options: ${error.message}`);
    }
};

// Fetch models by brand
export const fetchModelsByBrand = async (brand) => {
    console.log(`FETCH MODELS FOR BRAND: ${brand}`);

    try {
        // Try to use dedicated endpoint if it exists
        const config = getAuthConfig();

        try {
            const response = await axios.get(`${BASE_URL}/assets/models/${brand}`, config);
            console.log("Models response:", response.data);
            return response.data;
        } catch (endpointError) {
            // If endpoint doesn't exist, filter from all assets
            console.log("Dedicated endpoint not available, filtering all assets");
            const allAssets = await fetchAssetOptions();
            const filteredModels = allAssets.filter(asset =>
                asset.brand && asset.brand.toLowerCase() === brand.toLowerCase()
            );
            console.log(`Found ${filteredModels.length} models for brand ${brand}`);
            return filteredModels;
        }
    } catch (error) {
        console.error(`Error fetching models for brand ${brand}:`, error);
        throw error;
    }
};

// UPDATED: Fetch equipment details by model using assets endpoint
export const fetchEquipmentByModel = async (brand, model) => {
    console.log(`FETCH EQUIPMENT FOR BRAND: ${brand}, MODEL: ${model}`);

    try {
        const config = getAuthConfig();

        // Use the assets endpoint that filters for available equipment
        const response = await axios.get(`${BASE_URL}/assets/equipment/${brand}/${model}`, config);
        console.log("Equipment response:", response.data);

        // Log detailed structure of first item for debugging
        if (response.data && response.data.length > 0) {
            const sampleItem = response.data[0];
            console.log("Sample equipment item structure:", {
                id: sampleItem.id,
                serial_number: sampleItem.serial_number,
                status: sampleItem.status,
                allKeys: Object.keys(sampleItem)
            });
        }

        // The backend endpoint should already filter for available items
        // But we'll double-check with additional filtering
        const availableEquipment = response.data.filter(item =>
            item.status === "In Stock" ||
            item.status === "Not Used" ||
            item.status === "Available" ||
            item.status === "Ready"
        );

        console.log(`Found ${availableEquipment.length} available equipment items out of ${response.data.length} total`);

        // Return only available equipment to prevent assignment issues
        return availableEquipment;
    } catch (error) {
        console.error(`Error fetching equipment for ${brand} ${model}:`, error);
        // Return empty array if 404 (no equipment found)
        if (error.response?.status === 404) {
            return [];
        }
        throw error;
    }
};